import { useEffect, useState } from 'react';
import { api, type CardVersion } from '../lib/api';

// ---------------------------------------------------------------------------
// Where this card has been.
//
// A creator who can go back will experiment. One who cannot will be careful,
// and careful is slower and worse — so this exists to make the risk cheap
// rather than to be admired.
//
// Restoring writes the old state FORWARD as the current one rather than
// deleting anything after it, so going back is itself a version and going
// forward again is the same operation. There is no redo button because there is
// nothing for it to do that this list does not already.
// ---------------------------------------------------------------------------

function when(iso: string): string {
  const then = new Date(iso).getTime();
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString();
}

export function History({ cardId, reloadKey, onRestored }: {
  cardId: string;
  /** Changes whenever the card is saved, so the list follows the work. */
  reloadKey: unknown;
  onRestored: () => void | Promise<void>;
}) {
  const [versions, setVersions] = useState<CardVersion[]>([]);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.versions(cardId)
      .then((v) => { if (live) setVersions(v); })
      .catch(() => { if (live) setVersions([]); });
    return () => { live = false; };
  }, [cardId, reloadKey]);

  if (!versions.length) {
    return (
      <p className="hint">
        No history yet. Every save from here is a version you can come back to.
      </p>
    );
  }

  return (
    <div className="history">
      {error && <p className="director-error">{error}</p>}
      {versions.map((v, i) => (
        <div className={`ver${i === 0 ? ' is-current' : ''}`} key={v.id}>
          <span className="ver-seq">{v.seq}</span>
          <div className="ver-what">
            <b>{v.label}</b>
            <em>
              {when(v.created_at)}
              {/* The honest version of "instant". Reuse looks at the most
                  recent job for a key, so a configuration whose last attempt
                  failed gets a fresh render — this says which ones are free
                  rather than promising it of all of them. */}
              {v.rendered ? ' · video ready' : ''}
            </em>
          </div>
          {i === 0 ? (
            <span className="ver-now">now</span>
          ) : (
            <button
              className="btn tiny"
              disabled={busy !== null}
              title={v.rendered
                ? 'Go back to this — its finished video comes back with it, at no cost'
                : 'Go back to this. It has never been rendered, so a render would start from scratch.'}
              onClick={() => {
                setBusy(v.seq); setError(null);
                api.restoreVersion(cardId, v.seq)
                  .then(() => onRestored())
                  .catch((e) => setError(e instanceof Error ? e.message : 'could not restore that'))
                  .finally(() => setBusy(null));
              }}
            >
              {busy === v.seq ? 'Going back…' : 'Restore'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
